/**
 * Live mode's wire, version 2: what a capable client tells the server it can
 * do, and the frames the two sides add to the assistant's live call
 * (`GET /v1/agent/assistant/live`) once it has. A client that sends no
 * `capabilities` in its `start` is legacy and never sees these frames; one
 * that does gets them beside the frames it already reads
 * (docs/plans/os-voice-conversation.md, The conversation contract).
 *
 * The readers here are as loose as the shipped one: a frame this build does
 * not know, or one with a wrong value, is null, never a crash. They also hold
 * every frame to bounded sizes, so a frame cannot carry a page of text or a
 * thousand ids.
 */
import type { UnderstandingOutput } from './agent';

/** The longest text a frame carries; longer text is cut. */
export const LIVE_TEXT_MAX = 2000;
/** The longest id a frame carries (as `TOOL_CALL_ID_MAX`); a longer one is not an id. */
export const LIVE_ID_MAX = 200;
/** The most ids an `attention` frame names as in view; the rest are cut. */
export const LIVE_IN_VIEW_MAX = 50;

/** The most parts a `drafting` frame names; the rest are cut. */
export const LIVE_DRAFT_GHOSTS_MAX = 20;
/** The longest name a drafting part carries; longer is cut. */
export const LIVE_GHOST_NAME_MAX = 80;
/** The most canvas keys a `setup_scope` frame names; the rest are cut. */
export const LIVE_SCOPE_MAX = 10;

/**
 * A part of an accepted draft version that the compiler is still filling in,
 * drawn `drawing` on the studio's canvas. Never a card before the server
 * accepts it (G2).
 */
export type LiveDraftGhost = {
  readonly key: string;
  readonly name: string;
  readonly kind: 'record' | 'list' | 'link' | 'card' | 'role';
};
export const LIVE_DRAFT_GHOST_KINDS: readonly LiveDraftGhost['kind'][] = ['record', 'list', 'link', 'card', 'role'];

/** What the studio's open question wants typed: the client opens Keyboard for it. */
export type LiveAskHint = 'name' | 'phone' | 'url' | 'number';
export const LIVE_ASK_HINTS: readonly LiveAskHint[] = ['name', 'phone', 'url', 'number'];

/**
 * What the setup app tells the voice of its own progress (`app_note`).
 * `stage` is the studio's own stage as the page stands (`LIVE_STUDIO_STAGES`,
 * the text), sent whenever it changes and again once the call is back after a
 * drop: the server mirrors it and says nothing.
 */
export const LIVE_APP_NOTE_KINDS = ['build_step', 'build_done', 'built', 'proposal', 'sheet', 'sheet_closed', 'source', 'stage'] as const;

/** Where the studio's setup stands on the page: drafting, its Build sheet open, building, or built. */
export const LIVE_STUDIO_STAGES = ['drafting', 'sheet', 'building', 'built'] as const;
export type LiveStudioStage = (typeof LIVE_STUDIO_STAGES)[number];
export const isLiveStudioStage = (value: unknown): value is LiveStudioStage => typeof value === 'string' && (LIVE_STUDIO_STAGES as readonly string[]).includes(value);
export type LiveAppNoteKind = (typeof LIVE_APP_NOTE_KINDS)[number];

/**
 * The studio's setup session a live call opens on (`start.context.setupSession`),
 * and the tab it opens from (`client`, its `X-Setup-Client`: the call holds
 * the setup's lease for that place, step 9.4 of onboarding-harness phase 9).
 */
export type LiveSetupSession = { readonly id: string; readonly client?: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A `start` context's `setupSession`, read: an object whose id is a uuid, or null (the legacy seat). */
const PLACE = /^[\w-]{1,64}$/;

export const readLiveSetupSession = (value: unknown): LiveSetupSession | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const { id, client } = value as { id?: unknown; client?: unknown };
  if (typeof id !== 'string' || !UUID.test(id)) return null;
  return typeof client === 'string' && PLACE.test(client) ? { id, client } : { id };
};

/** Which client opened the call. The web-only spoken lines need it. */
export type LivePlatform = 'phone' | 'web';

/**
 * What a capable client can do, sent in `start`'s context. `duplex` is what
 * the client can carry: `full` keeps the mic sending while OS speaks, `half`
 * gates it. `device` is a short human name (the phone's model, or "Chrome on
 * Mac") another device's "Continue live here" names.
 */
export type LiveCapabilities = {
  readonly protocol: 2;
  readonly platform: LivePlatform;
  readonly duplex: 'full' | 'half';
  readonly echoCancel: boolean;
  readonly vad: boolean;
  readonly attention: boolean;
  readonly device?: string;
};

/**
 * How readily OS takes speech as its own (option B step 6, decided 7 Oct
 * 2026): `name_only` ("Only when I say OS": its name, an orb tap, a reply to
 * its own question; no conversation window), `normal` (the default: the
 * window and the classifier), `everything` ("Everything near me": it leans
 * towards answering in the window and out of it, and business talk with no
 * cue still needs one, G3). Sent in `start`'s context and changeable
 * mid-call by a `sensitivity` frame.
 */
export const LIVE_SENSITIVITIES = ['name_only', 'normal', 'everything'] as const;
export type LiveSensitivity = (typeof LIVE_SENSITIVITIES)[number];
export const DEFAULT_LIVE_SENSITIVITY: LiveSensitivity = 'normal';

/** A sensitivity, read: one of the three, or null for anything else. */
export const readLiveSensitivity = (value: unknown): LiveSensitivity | null =>
  typeof value === 'string' && (LIVE_SENSITIVITIES as readonly string[]).includes(value) ? (value as LiveSensitivity) : null;

/** The seat a live call opens on: the assistant, or setup (a new workspace, or Edit the setup, on its session). */
export type LiveSeat = 'assistant' | 'setup';

/** The call's turn taking as the server runs it: today's windows, client-gated half duplex, or full duplex. */
export type LiveDuplexMode = 'legacy' | 'half' | 'full';
export const LIVE_DUPLEX_MODES: readonly LiveDuplexMode[] = ['legacy', 'half', 'full'];

/** Why live mode ended. The divider words each one (live-ending.ts). */
export const LIVE_END_REASONS = [
  'stopped',
  'closed',
  'idle',
  'time',
  'moved',
  'quota',
  'lost',
  'provider',
  'phone_gone',
  'refused',
  'background',
  'interrupted',
] as const;
export type LiveEndReason = (typeof LIVE_END_REASONS)[number];
export const isLiveEndReason = (value: unknown): value is LiveEndReason =>
  typeof value === 'string' && (LIVE_END_REASONS as readonly string[]).includes(value);

/** The live state the server owns; the client adds mute, connection and the rest (live-state.ts). */
export const LIVE_SERVER_STATES = [
  'listening',
  'working',
  'speaking',
  'parked',
  'quiet',
  'needs_tap',
  'still_there',
  'rollover',
  'reconnecting',
] as const;
export type LiveServerState = (typeof LIVE_SERVER_STATES)[number];

/** The conversation window as the server says it: open, and how long it has left (`LIVE_WINDOW_ENDS_MAX` at most). */
export type LiveWindow = { readonly open: boolean; readonly endsInMs?: number };

/** The longest a window's `endsInMs` may say: a minute. Longer is not a window. */
export const LIVE_WINDOW_ENDS_MAX = 60_000;

/** What one of OS's spoken lines is: the greeting, an acknowledgement, a fixed line, small talk, or an answer's gist. */
export const LIVE_LINE_KINDS = ['greeting', 'ack', 'say', 'gist', 'fixed'] as const;
export type LiveLineKind = (typeof LIVE_LINE_KINDS)[number];

/** How a person's line arrived. */
export type LiveVia = 'voice' | 'typed';

/** Server to a capable client, beside the frames every client reads. */
export type LiveServerFrame =
  /**
   * The server's live state; `endsInMs` counts down Still there? and the
   * roll-over. `window` is the conversation window while it is open (option
   * B, 7 Oct 2026): a follow-up said in it needs no "OS", and the orb wears a
   * faint open ring. Absent is closed.
   */
  | { readonly type: 'state'; readonly state: LiveServerState; readonly endsInMs?: number; readonly window?: LiveWindow }
  /**
   * One line of the live transcript, whole each time under its id: replace,
   * never append. The person's addressed words (`role: 'user'`, faint until
   * `final`) or one of OS's spoken lines.
   */
  | {
      readonly type: 'line';
      readonly id: string;
      readonly role: 'user' | 'assistant';
      readonly text: string;
      readonly final: boolean;
      readonly kind?: LiveLineKind;
      readonly via?: LiveVia;
      /** A gist names the Coach reply it speaks for. */
      readonly replyTo?: string;
      /** When it happened, by the server's clock in ms: OS's line as it opened, the person's as its last words came (spoken-order.ts). */
      readonly at?: number;
      /** The person's settled line: the language it was said in, when its letters are not the ones it was said in (Devanagari in Roman on a Hinglish app). */
      readonly spoken?: 'en' | 'hi' | 'hi-Latn';
    }
  /** The one visible Next, or none (`id: null`). */
  | { readonly type: 'next'; readonly id: string; readonly text: string; readonly via: LiveVia }
  | { readonly type: 'next'; readonly id: null }
  /** A running request was replaced by a correction: its card is marked Replaced. */
  | { readonly type: 'replaced'; readonly messageId: string; readonly by: string }
  /** A spoken stop parked a running request: Resume brings it back. */
  | { readonly type: 'parked'; readonly messageId: string }
  /** Whether a card can be saved by voice, or needs a tap. */
  | { readonly type: 'card_voice'; readonly toolCallId: string; readonly voice: 'ok' | 'tap_only' }
  /**
   * The setup seat's note on a line: the understanding so far, whether Build
   * can light, and `build` when the line was a spoken Build ("bana do") the
   * client acts on.
   */
  | {
      readonly type: 'note';
      readonly lineId: string;
      readonly understanding?: UnderstandingOutput | null;
      readonly ready?: boolean;
      readonly build?: true;
    }
  /** A live call runs on another device: its name and how long it has been live. */
  | { readonly type: 'elsewhere'; readonly device?: string; readonly sinceMs: number }
  /**
   * The person seems to be talking to OS and is not being heard as such: two
   * utterances in a row were judged not for OS after they had spoken to it
   * this session. The caption says "Listening · say “OS” to ask" for a few
   * seconds (`LIVE_NAME_HINT_SHOW_MS`). Carries no words (7 Oct evening, item 4).
   */
  | { readonly type: 'name_hint' }
  /**
   * The person's last utterance ended unsure, or not for OS just as the
   * conversation window closed (option B, step 5): the client shows a dimmed
   * "Was that for me?" chip for `LIVE_CLAIM_SHOW_MS`, with no words of what
   * was said, and a tap sends `claim` with this id. Carries the id only.
   */
  | { readonly type: 'unsure'; readonly utteranceId: string }
  /**
   * The person's utterance settled and no line of it will be drawn: it was
   * judged not for OS, or unsure, or had no words (12 Oct). The client takes
   * away the typing dots it drew while the person spoke. Carries the id only:
   * side talk never shows.
   */
  | { readonly type: 'passed'; readonly utteranceId: string }
  /**
   * What the transcriber hears, on a full-duplex call only: the client's
   * pause rule (`pauseOf`) and self-echo detector (`selfEchoOf`) read it while
   * OS plays. It is never drawn, kept or logged: side talk never shows, and
   * only an addressed line becomes a `line`.
   */
  | { readonly type: 'partial'; readonly text: string; readonly final: boolean }
  /*
   * The studio seat's frames, sent only on a call whose `start` carried
   * `setupSession` (onboarding-harness 7.2).
   */
  /** Parts of an accepted version the drafter is still filling in for this line; `ghosts: []` clears them. */
  | { readonly type: 'drafting'; readonly lineId: string; readonly ghosts: readonly LiveDraftGhost[] }
  /** The drafter is reading or looking something up (`what`, a short human name); `what: null` ends the row. */
  | { readonly type: 'work'; readonly lineId: string; readonly what: string | null; readonly done?: number; readonly total?: number }
  /** The drafter's turn for the line ended: the new draft version, or null when nothing changed. */
  | { readonly type: 'drafted'; readonly lineId: string; readonly version: number | null; readonly refused?: string }
  /** OS's line `lineId` is about the canvas card `key`. */
  | { readonly type: 'point'; readonly key: string; readonly lineId: string }
  /** The open question wants typing: the client opens Keyboard. */
  | { readonly type: 'ask_hint'; readonly hint: LiveAskHint }
  /** The person asked to build by voice: the Build sheet opens. `open: false` is reserved. */
  | { readonly type: 'build_sheet'; readonly open: boolean; readonly by: 'voice' }
  /** After Build, the person said to open the workspace. */
  | { readonly type: 'open_workspace' };

/** Client to server, from a capable client, beside the frames every client sends. */
export type LiveClientFrame =
  /**
   * Whether the person is looking (null: unknown), the app's state, the
   * card ids on screen, and when they last touched it.
   */
  | {
      readonly type: 'attention';
      readonly looking: boolean | null;
      readonly app: 'foreground' | 'background';
      readonly inView: readonly string[];
      readonly lastTouchMs: number;
      readonly screenReader?: boolean;
    }
  /** What of OS's voice has played, so the model is told what the person heard. */
  | { readonly type: 'playback'; readonly messageId?: string; readonly playedMs: number; readonly state: 'speaking' | 'paused' | 'idle' }
  /** The client's voice activity: someone speaking, near the device, and the level (0–1). */
  | { readonly type: 'vad'; readonly speaking: boolean; readonly near: boolean | null; readonly level: number }
  /** The client dropped to half duplex (its echo detector fired) or came back to full. */
  | { readonly type: 'duplex'; readonly mode: 'full' | 'half' }
  /** The system took the audio: a phone call, a route change, or anything else. */
  | { readonly type: 'pause'; readonly why: 'phone_call' | 'route' | 'system' }
  | { readonly type: 'resume' }
  | { readonly type: 'quiet'; readonly on: boolean }
  /** Carry on past the 30-minute roll-over. */
  | { readonly type: 'carry_on' }
  /** Bring back the parked request. */
  | { readonly type: 'resume_work' }
  /** The orb was tapped: what comes next is for OS. */
  | { readonly type: 'attend' }
  /** "Was that for me?" was tapped: the utterance it named was for OS after all (option B, step 5). */
  | { readonly type: 'claim'; readonly utteranceId: string }
  /** The person changed the sensitivity in Account while live (option B, step 6). */
  | { readonly type: 'sensitivity'; readonly level: LiveSensitivity }
  /** The setup app's own progress, for the voice to know of. */
  | { readonly type: 'app_note'; readonly kind: LiveAppNoteKind; readonly text: string }
  /** The studio's canvas selection changed (at most `LIVE_SCOPE_MAX` keys; `[]` cleared). */
  | { readonly type: 'setup_scope'; readonly keys: readonly string[] };

/* --------------------------------------------------------------- readers */

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parsed = (raw: string): unknown => {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
};

const oneOf = <T extends string>(values: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (values as readonly string[]).includes(value);

/** A non-empty string no longer than an id may be. */
const isId = (value: unknown): value is string => typeof value === 'string' && value !== '' && value.length <= LIVE_ID_MAX;

/** A finite number of zero or more. */
const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;

const textOf = (value: string): string => value.slice(0, LIVE_TEXT_MAX);

/** A whole number of zero or more. */
const isWhole = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;

/** One drafting part, read: none when its key, name or kind is wrong; a long name is cut. */
const ghostOf = (value: unknown): LiveDraftGhost[] =>
  isObject(value) && isId(value.key) && typeof value.name === 'string' && oneOf(LIVE_DRAFT_GHOST_KINDS, value.kind)
    ? [{ key: value.key, name: value.name.slice(0, LIVE_GHOST_NAME_MAX), kind: value.kind }]
    : [];

/** A `window` field, read: open with its time left, or closed; null for anything else. */
const windowOf = (value: unknown): LiveWindow | null => {
  if (!isObject(value) || typeof value.open !== 'boolean') return null;
  const ends = value.endsInMs;
  return typeof ends === 'number' && Number.isFinite(ends) && ends >= 0 && ends <= LIVE_WINDOW_ENDS_MAX ? { open: value.open, endsInMs: ends } : { open: value.open };
};

/**
 * A capable client's `capabilities`, or null when absent, partial or wrongly
 * typed: null means the call is legacy. A device name that is not a short
 * string is dropped; the rest still reads.
 */
export const readLiveCapabilities = (value: unknown): LiveCapabilities | null => {
  if (!isObject(value)) return null;
  if (value.protocol !== 2) return null;
  if (!oneOf<LivePlatform>(['phone', 'web'], value.platform)) return null;
  if (value.duplex !== 'full' && value.duplex !== 'half') return null;
  if (typeof value.echoCancel !== 'boolean' || typeof value.vad !== 'boolean' || typeof value.attention !== 'boolean') return null;
  return {
    protocol: 2,
    platform: value.platform,
    duplex: value.duplex,
    echoCancel: value.echoCancel,
    vad: value.vad,
    attention: value.attention,
    ...(isId(value.device) ? { device: value.device } : {}),
  };
};

const understandingOf = (value: unknown): UnderstandingOutput | null | undefined => {
  if (value === null) return null;
  if (!isObject(value) || value.kind !== 'understanding' || !isObject(value.understanding) || !isObject(value.coverage)) return undefined;
  return value as unknown as UnderstandingOutput;
};

/** One version-2 server frame from its parsed JSON; null for anything else. */
export const readLiveServerFrame = (body: unknown): LiveServerFrame | null => {
  if (!isObject(body)) return null;
  switch (body.type) {
    case 'state': {
      if (!oneOf(LIVE_SERVER_STATES, body.state)) return null;
      const window = windowOf(body.window);
      return { type: 'state', state: body.state, ...(isCount(body.endsInMs) ? { endsInMs: body.endsInMs } : {}), ...(window === null ? {} : { window }) };
    }
    case 'line':
      if (!isId(body.id) || (body.role !== 'user' && body.role !== 'assistant') || typeof body.text !== 'string') return null;
      return {
        type: 'line',
        id: body.id,
        role: body.role,
        text: textOf(body.text),
        final: body.final === true,
        ...(oneOf(LIVE_LINE_KINDS, body.kind) ? { kind: body.kind } : {}),
        ...(oneOf<LiveVia>(['voice', 'typed'], body.via) ? { via: body.via } : {}),
        ...(isId(body.replyTo) ? { replyTo: body.replyTo } : {}),
        ...(typeof body.at === 'number' && Number.isFinite(body.at) ? { at: body.at } : {}),
        ...(body.role === 'user' && (body.spoken === 'en' || body.spoken === 'hi' || body.spoken === 'hi-Latn') ? { spoken: body.spoken } : {}),
      };
    case 'next':
      if (body.id === null) return { type: 'next', id: null };
      return isId(body.id) && typeof body.text === 'string' && oneOf<LiveVia>(['voice', 'typed'], body.via)
        ? { type: 'next', id: body.id, text: textOf(body.text), via: body.via }
        : null;
    case 'replaced':
      return isId(body.messageId) && isId(body.by) ? { type: 'replaced', messageId: body.messageId, by: body.by } : null;
    case 'parked':
      return isId(body.messageId) ? { type: 'parked', messageId: body.messageId } : null;
    case 'name_hint':
      return { type: 'name_hint' };
    case 'unsure':
      return isId(body.utteranceId) ? { type: 'unsure', utteranceId: body.utteranceId } : null;
    case 'passed':
      return isId(body.utteranceId) ? { type: 'passed', utteranceId: body.utteranceId } : null;
    case 'card_voice':
      return isId(body.toolCallId) && (body.voice === 'ok' || body.voice === 'tap_only')
        ? { type: 'card_voice', toolCallId: body.toolCallId, voice: body.voice }
        : null;
    case 'note': {
      if (!isId(body.lineId)) return null;
      const understanding = understandingOf(body.understanding);
      return {
        type: 'note',
        lineId: body.lineId,
        ...(understanding === undefined ? {} : { understanding }),
        ...(typeof body.ready === 'boolean' ? { ready: body.ready } : {}),
        ...(body.build === true ? { build: true as const } : {}),
      };
    }
    case 'elsewhere':
      return isCount(body.sinceMs) ? { type: 'elsewhere', ...(isId(body.device) ? { device: body.device } : {}), sinceMs: body.sinceMs } : null;
    case 'partial':
      return typeof body.text === 'string' ? { type: 'partial', text: textOf(body.text), final: body.final === true } : null;
    case 'drafting':
      return isId(body.lineId) && Array.isArray(body.ghosts)
        ? { type: 'drafting', lineId: body.lineId, ghosts: body.ghosts.flatMap((ghost) => ghostOf(ghost)).slice(0, LIVE_DRAFT_GHOSTS_MAX) }
        : null;
    case 'work':
      if (!isId(body.lineId) || (body.what !== null && typeof body.what !== 'string')) return null;
      return {
        type: 'work',
        lineId: body.lineId,
        what: body.what === null ? null : textOf(body.what),
        ...(isWhole(body.done) ? { done: body.done } : {}),
        ...(isWhole(body.total) ? { total: body.total } : {}),
      };
    case 'drafted':
      if (!isId(body.lineId) || (body.version !== null && !isWhole(body.version))) return null;
      return {
        type: 'drafted',
        lineId: body.lineId,
        version: body.version,
        ...(typeof body.refused === 'string' && body.refused !== '' ? { refused: textOf(body.refused) } : {}),
      };
    case 'point':
      return isId(body.key) && isId(body.lineId) ? { type: 'point', key: body.key, lineId: body.lineId } : null;
    case 'ask_hint':
      return oneOf(LIVE_ASK_HINTS, body.hint) ? { type: 'ask_hint', hint: body.hint } : null;
    case 'build_sheet':
      return typeof body.open === 'boolean' && body.by === 'voice' ? { type: 'build_sheet', open: body.open, by: 'voice' } : null;
    case 'open_workspace':
      return { type: 'open_workspace' };
    default:
      return null;
  }
};

/** One version-2 client frame, from the string the socket delivers or its parsed JSON; null for anything else. */
export const readLiveClientFrame = (raw: unknown): LiveClientFrame | null => {
  const body = typeof raw === 'string' ? parsed(raw) : raw;
  if (!isObject(body)) return null;
  switch (body.type) {
    case 'attention': {
      if (body.looking !== null && typeof body.looking !== 'boolean') return null;
      if (body.app !== 'foreground' && body.app !== 'background') return null;
      if (!Array.isArray(body.inView) || !isCount(body.lastTouchMs)) return null;
      return {
        type: 'attention',
        looking: body.looking,
        app: body.app,
        inView: body.inView.filter(isId).slice(0, LIVE_IN_VIEW_MAX),
        lastTouchMs: body.lastTouchMs,
        ...(typeof body.screenReader === 'boolean' ? { screenReader: body.screenReader } : {}),
      };
    }
    case 'playback':
      return isCount(body.playedMs) && oneOf(['speaking', 'paused', 'idle'] as const, body.state)
        ? { type: 'playback', ...(isId(body.messageId) ? { messageId: body.messageId } : {}), playedMs: body.playedMs, state: body.state }
        : null;
    case 'vad':
      // `near` null or absent is unknown: a client that cannot judge it says so.
      return typeof body.speaking === 'boolean' && (typeof body.near === 'boolean' || body.near === null || body.near === undefined) && typeof body.level === 'number' && Number.isFinite(body.level)
        ? { type: 'vad', speaking: body.speaking, near: typeof body.near === 'boolean' ? body.near : null, level: Math.min(1, Math.max(0, body.level)) }
        : null;
    case 'duplex':
      return body.mode === 'full' || body.mode === 'half' ? { type: 'duplex', mode: body.mode } : null;
    case 'pause':
      return oneOf(['phone_call', 'route', 'system'] as const, body.why) ? { type: 'pause', why: body.why } : null;
    case 'quiet':
      return typeof body.on === 'boolean' ? { type: 'quiet', on: body.on } : null;
    case 'resume':
    case 'carry_on':
    case 'resume_work':
    case 'attend':
      return { type: body.type };
    case 'claim':
      return isId(body.utteranceId) ? { type: 'claim', utteranceId: body.utteranceId } : null;
    case 'sensitivity': {
      const level = readLiveSensitivity(body.level);
      return level === null ? null : { type: 'sensitivity', level };
    }
    case 'app_note':
      return oneOf(LIVE_APP_NOTE_KINDS, body.kind) && typeof body.text === 'string'
        ? { type: 'app_note', kind: body.kind, text: textOf(body.text) }
        : null;
    case 'setup_scope':
      return Array.isArray(body.keys) ? { type: 'setup_scope', keys: body.keys.filter(isId).slice(0, LIVE_SCOPE_MAX) } : null;
    default:
      return null;
  }
};
